import '../styles/NotebookPage.css';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { NOTE_TEMPLATES } from '../utils/constants.js';
import { exportTXT, exportPDF, sanitizeHTML, serializeNoteHTML } from '../utils/helpers.js';
import { api } from '../utils/api.js';
/* ─────────────────────────────────────────────────────────────
   CONSTANTS & CONFIG
───────────────────────────────────────────────────────────────*/
const LS_TAGS = 'kfx_note_tags';
const SORT_OPTIONS = ['Updated','Created','Title','A-Z'];
const TAG_COLORS   = [
  '#C9A84C','#3DBE73','#4E7FD4','#D94F4F',
  '#FF6B35','#E2A542','#9B59B6','#1ABC9C',
];

const loadTags = () => { try { const s = localStorage.getItem(LS_TAGS); return s ? JSON.parse(s) : []; } catch { return []; } };

/* ── API helpers ── */
function dbToNote(n) {
  return {
    id:      n.id,
    title:   n.title   || '',
    body:    n.content || '',
    tagIds:  [],
    pinned:  n.pinned  || false,
    created: n.createdAt || n.created_at || '',
    updated: n.updatedAt || n.updated_at || '',
    history: [],
  };
}

async function apiCreateNote({ title, content, pinned = false }) {
  return api('/notebooks', { method: 'POST', body: JSON.stringify({ title: title || 'Untitled Note', content: content || '', pinned }) });
}
async function apiUpdateNote(id, { title, content, pinned }) {
  return api(`/notebooks/${id}`, { method: 'PUT', body: JSON.stringify({ title, content, pinned }) });
}
async function apiDeleteNote(id) {
  return api(`/notebooks/${id}`, { method: 'DELETE' });
}
async function apiListNotes() {
  return api('/notebooks?limit=100');
}

/* ─────────────────────────────────────────────────────────────
   TOAST SYSTEM
───────────────────────────────────────────────────────────────*/
const TOAST_ICONS = {
  success: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>,
  error:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
  info:    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>,
};

const TOAST_STYLES = {
  success: { border:'1px solid rgba(80,200,120,.35)', background:'linear-gradient(135deg,rgba(18,26,22,.97),rgba(14,22,18,.97))', iconBg:'rgba(80,200,120,.15)', iconColor:'#50c878', bar:'#50c878' },
  error:   { border:'1px solid rgba(217,79,79,.35)',  background:'linear-gradient(135deg,rgba(26,16,16,.97),rgba(22,14,14,.97))',  iconBg:'rgba(217,79,79,.15)',  iconColor:'var(--red)', bar:'var(--red)' },
  info:    { border:'1px solid rgba(201,168,76,.3)',   background:'linear-gradient(135deg,rgba(22,20,14,.97),rgba(18,16,10,.97))',  iconBg:'rgba(201,168,76,.12)', iconColor:'var(--gold)', bar:'var(--gold)' },
};

function Toast({ id, type='info', title, message, duration=4000, onDismiss }) {
  const [phase, setPhase] = useState('enter');
  const [progress, setProgress] = useState(100);
  const startRef   = useRef(null);
  const rafRef     = useRef(null);
  const dismissRef = useRef(null);

  const dismiss = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    setPhase('exit');
    setTimeout(() => onDismiss(id), 350);
  }, [id, onDismiss]);

  dismissRef.current = dismiss;

  useEffect(() => {
    const t = setTimeout(() => setPhase('idle'), 20);
    startRef.current = performance.now();
    const tick = now => {
      const pct = Math.max(0, 100 - ((now - startRef.current) / duration) * 100);
      setProgress(pct);
      if (pct > 0) rafRef.current = requestAnimationFrame(tick);
      else dismissRef.current();
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { clearTimeout(t); cancelAnimationFrame(rafRef.current); };
  }, [duration]);

  const s = TOAST_STYLES[type];
  const slideStyle = {
    enter: { opacity:0, transform:'translateX(calc(100% + 20px)) scale(0.95)' },
    idle:  { opacity:1, transform:'translateX(0) scale(1)' },
    exit:  { opacity:0, transform:'translateX(calc(100% + 20px)) scale(0.92)' },
  }[phase];

  return (
    <div
      className="nb-toast"
      onClick={dismiss}
      style={{ background:s.background, border:s.border, boxShadow:'0 8px 32px rgba(0,0,0,.6)', ...slideStyle }}
    >
      <div className="nb-toast-bar" style={{ width:`${progress}%`, background:s.bar }}/>
      <div className="nb-toast-inner">
        <div className="nb-toast-icon-wrap" style={{ background:s.iconBg, color:s.iconColor }}>{TOAST_ICONS[type]}</div>
        <div className="nb-toast-content">
          {title   && <div className={`nb-toast-title${message?' has-msg':''}`}>{title}</div>}
          {message && <div className="nb-toast-message">{message}</div>}
        </div>
        <button className="nb-toast-close" onClick={e=>{e.stopPropagation();dismiss();}}>×</button>
      </div>
    </div>
  );
}

function ToastStack({ toasts, onDismiss }) {
  return (
    <div className="nb-toast-stack">
      {toasts.map(t => <div key={t.id} className="nb-toast-item"><Toast {...t} onDismiss={onDismiss}/></div>)}
    </div>
  );
}

function useToasts() {
  const [toasts, setToasts] = useState([]);
  const push    = useCallback(({ type='info', title, message, duration=4000 }) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, type, title, message, duration }]);
  }, []);
  const dismiss = useCallback(id => setToasts(prev => prev.filter(t => t.id !== id)), []);
  return { toasts, push, dismiss };
}

/* ─────────────────────────────────────────────────────────────
   DELETE CONFIRM MODAL
───────────────────────────────────────────────────────────────*/
function ConfirmModal({ noteTitle, onConfirm, onCancel }) {
  return (
    <div className="nb-confirm-overlay">
      <div className="nb-confirm-box">
        <div className="nb-confirm-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--red)" strokeWidth="2">
            <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
          </svg>
        </div>
        <div className="nb-confirm-title">Delete Note?</div>
        <div className="nb-confirm-body">
          This will permanently delete <span className="nb-confirm-note-name">"{noteTitle || 'Untitled'}"</span>. This action cannot be undone.
        </div>
        <div className="nb-confirm-actions">
          <button onClick={onCancel} className="nb-confirm-cancel">Cancel</button>
          <button onClick={onConfirm} className="nb-confirm-delete">Delete</button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   TAG BADGE
───────────────────────────────────────────────────────────────*/
function TagBadge({ label, color, onRemove, small }) {
  return (
    <span
      className={`nb-tag-badge ${small ? 'small' : 'normal'}`}
      style={{ background:`${color}16`, border:`1px solid ${color}3A`, color }}
    >
      {label}
      {onRemove && (
        <button onClick={onRemove} className="nb-tag-remove" style={{ color }}>×</button>
      )}
    </span>
  );
}

/* ─────────────────────────────────────────────────────────────
   TAG MANAGER MODAL
───────────────────────────────────────────────────────────────*/
function TagModal({ allTags, noteTags, onSave, onClose, onCreateTag }) {
  const [phase, setPhase]   = useState('enter');
  const [newTag, setNewTag] = useState('');
  const [selected, setSelected] = useState(new Set(noteTags));
  const [colorIdx, setColorIdx] = useState(0);

  useEffect(() => { const t = setTimeout(() => setPhase('idle'), 20); return () => clearTimeout(t); }, []);
  const close = () => { setPhase('exit'); setTimeout(onClose, 260); };

  const toggle = id => setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const createAndAdd = () => {
    const name = newTag.trim();
    if (!name || name.length > 20) return;
    const tag = onCreateTag(name, TAG_COLORS[colorIdx]);
    setSelected(prev => new Set([...prev, tag.id]));
    setNewTag('');
  };

  const overlayBg  = { background:`rgba(0,0,0,${phase==='idle'?0.72:0})`, transition:'background 260ms ease', backdropFilter:'blur(6px)' };
  const cardStyle  = {
    enter: { opacity:0, transform:'scale(0.93) translateY(10px)' },
    idle:  { opacity:1, transform:'scale(1) translateY(0)' },
    exit:  { opacity:0, transform:'scale(0.96) translateY(6px)' },
  }[phase];

  return (
    <div className="nb-tag-modal-overlay" style={overlayBg} onClick={close}>
      <div className="nb-tag-modal-box" onClick={e=>e.stopPropagation()} style={{ transition:'opacity 260ms cubic-bezier(.4,0,.2,1),transform 260ms cubic-bezier(.4,0,.2,1)', ...cardStyle }}>
        <div className="nb-tag-modal-header">
          <div className="nb-tag-modal-title">Manage Tags</div>
          <button onClick={close} className="nb-tag-modal-close">×</button>
        </div>

        <div className="nb-tag-create-section">
          <div className="nb-tag-create-label">Create New Tag</div>
          <div className="nb-tag-color-row">
            {TAG_COLORS.map((c,i) => (
              <button
                key={c}
                onClick={()=>setColorIdx(i)}
                className="nb-tag-color-swatch"
                style={{ background:c, border:i===colorIdx?`2px solid ${c}`:'2px solid transparent', outline:i===colorIdx?`2px solid rgba(255,255,255,.25)`:'none', boxShadow:i===colorIdx?`0 0 6px ${c}`:'none' }}
              />
            ))}
          </div>
          <div className="nb-tag-input-row">
            <input
              value={newTag}
              onChange={e=>setNewTag(e.target.value)}
              onKeyDown={e=>e.key==='Enter'&&createAndAdd()}
              placeholder="Tag name…"
              maxLength={20}
              className="nb-tag-name-input"
            />
            <button onClick={createAndAdd} className="nb-tag-add-btn">Add</button>
          </div>
        </div>

        <div className="nb-tag-divider"/>

        {allTags.length === 0 && <div className="nb-tag-empty">No tags yet. Create one above.</div>}
        <div className="nb-tag-list">
          {allTags.map(tag => {
            const on = selected.has(tag.id);
            return (
              <div
                key={tag.id}
                onClick={()=>toggle(tag.id)}
                className="nb-tag-list-item"
                style={{ background:on?`${tag.color}10`:'rgba(255,255,255,.02)', border:`1px solid ${on?tag.color+'44':'rgba(255,255,255,.06)'}` }}
              >
                <div className="nb-tag-list-dot" style={{ background:on?tag.color:'rgba(255,255,255,.1)', border:`2px solid ${on?tag.color:'rgba(255,255,255,.15)'}`, boxShadow:on?`0 0 6px ${tag.color}`:'' }}/>
                <span className={`nb-tag-list-name ${on?'selected':'unselected'}`}>{tag.name}</span>
                <TagBadge label={tag.name} color={tag.color} small/>
              </div>
            );
          })}
        </div>

        <div className="nb-tag-divider-spaced"/>
        <button onClick={()=>{ onSave([...selected]); close(); }} className="nb-tag-save-btn">Save Tags</button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   NOTE STATS PANEL
───────────────────────────────────────────────────────────────*/
function NoteStats({ editorRef, note }) {
  const txt   = editorRef?.current?.innerText || '';
  const words = txt.split(/\s+/).filter(Boolean).length;
  const chars = txt.replace(/\s/g,'').length;
  const mins  = Math.ceil(words / 200) || 1;

  const created = note?.created
    ? new Date(note.created).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})
    : '—';

  const updatedFmt = note?.updated
    ? new Date(note.updated).toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'})
    : '—';

  return (
    <div className="nb-title-stats">
      <span>{updatedFmt}</span>
      <span className="nb-title-stats-sep"> • </span>
      <span>{words} words</span>
      <span className="nb-title-stats-sep"> • </span>
      <span>{chars} chars</span>
      <span className="nb-title-stats-sep"> • </span>
      <span>{mins} min read</span>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   HISTORY PANEL
───────────────────────────────────────────────────────────────*/
function HistoryPanel({ history, onRestore, onClose }) {
  return (
    <div className="nb-history-panel">
      <div className="nb-history-header">
        <div className="nb-history-title">Version History</div>
        <button onClick={onClose} className="nb-history-close">×</button>
      </div>
      <div className="nb-history-list">
        {history.length === 0 && <div className="nb-history-empty">No saved versions yet.</div>}
        {history.map((v,i) => (
          <div key={v.ts} className="nb-history-item" onClick={()=>onRestore(v)}>
            <div className={`nb-history-item-label ${i===0?'latest':'past'}`}>
              {i===0?'Latest Save':new Date(v.ts).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit'})}
            </div>
            <div className="nb-history-item-date">{new Date(v.ts).toLocaleDateString('en-US',{month:'short',day:'numeric'})}</div>
            <div className="nb-history-item-preview">{(v.body||'').replace(/<[^>]+>/g,'').slice(0,60)||'—'}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   EXPORT DROPDOWN
───────────────────────────────────────────────────────────────*/
function ExportDropdown({ title, getBody, copyAsMarkdown, exportTXT, exportPDF, pushToast }) {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const wrapRef = useRef(null);
  const btnRef  = useRef(null);

  const openMenu = () => {
    if (!btnRef.current) { setOpen(v => !v); return; }
    const r = btnRef.current.getBoundingClientRect();
    setMenuStyle({ top: r.bottom + 6, right: window.innerWidth - r.right });
    setOpen(v => !v);
  };

  useEffect(() => {
    if (!open) return;
    const handler = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const run = fn => { setOpen(false); fn(); };

  return (
    <div ref={wrapRef} style={{ position:'relative', flexShrink:0 }}>
      <button ref={btnRef} className="nb-action-btn nb-action-gold" aria-haspopup="true" aria-expanded={open} onClick={openMenu}>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
        Export
        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ opacity:.45 }}><polyline points="6 9 12 15 18 9"/></svg>
      </button>

      {open && (
        <div className="nb-export-menu" style={{ display:'flex', flexDirection:'column', position:'fixed', top:menuStyle.top, right:menuStyle.right }}>
          <button className="nb-export-item" onClick={()=>run(()=>{ copyAsMarkdown(); })}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
            Copy as Markdown
          </button>
          <div className="nb-export-sep"/>
          <button className="nb-export-item" onClick={()=>run(()=>{ exportTXT(title,getBody()); pushToast({ type:'success', title:'TXT exported', message:'Plain-text file downloaded.', duration:3000 }); })}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/></svg>
            Export as TXT
          </button>
          <button className="nb-export-item" onClick={()=>run(()=>{ const ok=exportPDF(title,getBody()); ok ? pushToast({ type:'success', title:'PDF exported', message:'Opened in a new tab — use Save as PDF.', duration:4000 }) : pushToast({ type:'error', title:'PDF blocked', message:'Allow pop-ups for this site, then try again.', duration:6000 }); })}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
            Export as PDF
          </button>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   SKELETON LIST
───────────────────────────────────────────────────────────────*/
const SKEL_WIDTHS = [
  ['72%','55%'], ['55%','80%'], ['85%','40%'],
  ['60%','70%'], ['78%','50%'], ['50%','65%'],
];
function SkeletonList() {
  return (
    <>
      {SKEL_WIDTHS.map(([w1, w2], i) => (
        <div key={i} className="nb-skeleton-item" style={{ animationDelay:`${i*60}ms` }}>
          <div className="nb-skel nb-skel-title" style={{ width:w1 }}/>
          <div className="nb-skel nb-skel-preview" style={{ width:w2 }}/>
        </div>
      ))}
    </>
  );
}

/* ─────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────────*/
export default function NotebookPage({ prefill = null, onDraftsChange, onNoteCountChange }) {
  /* ── State ── */
  const [notes,         setNotes]         = useState([]);
  const [allTags,       setAllTags]       = useState(loadTags);
  const [activeId,      setActiveId]      = useState(null);
  const [mobileView,    setMobileView]    = useState('list');
  const [search,        setSearch]        = useState('');
  const [title,         setTitle]         = useState('');
  const [saveStatus,    setSaveStatus]    = useState('All saved');
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [showTagModal,  setShowTagModal]  = useState(false);
  const [showHistory,   setShowHistory]   = useState(false);
  const [sortBy,        setSortBy]        = useState('Updated');
  const [filterTagId,   setFilterTagId]   = useState(null);
  const [viewMode,      setViewMode]      = useState('list');
  const [wordGoal,      setWordGoal]      = useState(null);
  const [showGoalInput, setShowGoalInput] = useState(false);
  const [goalInputVal,  setGoalInputVal]  = useState('');
  const [isLoading,     setIsLoading]     = useState(true);

  const editorRef  = useRef(null);
  const imgInputRef = useRef(null);
  const saveTimer  = useRef(null);

  const { toasts, push: pushToast, dismiss: dismissToast } = useToasts();

  useEffect(() => { onDraftsChange?.(saveStatus === 'All saved' ? 0 : 1); }, [saveStatus, onDraftsChange]);
  useEffect(() => { onNoteCountChange?.(notes.length); }, [notes.length, onNoteCountChange]);
  useEffect(() => { try { localStorage.setItem(LS_TAGS, JSON.stringify(allTags)); } catch {} }, [allTags]);

  useEffect(() => {
    apiListNotes()
      .then(data => { const mapped = (data.notes || []).map(dbToNote); setNotes(mapped); })
      .catch(() => { pushToast({ type:'error', title:'Could not load notes', message:'Check your connection and refresh.', duration:5000 }); })
      .finally(() => setIsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!prefill?.prefillTrades?.length) return;
    const { prefillTrades, prefillDate } = prefill;

    const buildPrefillHTML = (trades, date) => {
      const totalPnl    = trades.reduce((s, t) => s + (Number(t.result) || 0), 0);
      const wins        = trades.filter(t => t.result > 0).length;
      const pnlColor    = totalPnl >= 0 ? '#3DBE73' : '#D94F4F';
      const pnlSign     = totalPnl >= 0 ? '+' : '';

      const tradeBlocks = trades.map(t => {
        const win       = t.result > 0;
        const pnl       = Number(t.result) || 0;
        const pnlStr    = `${pnl >= 0 ? '+' : '-'}$${Math.abs(pnl).toFixed(2)}`;
        const pnlClr    = win ? '#3DBE73' : '#D94F4F';
        const dirClr    = t.dir === 'BUY' ? '#3DBE73' : '#D94F4F';
        const rrStr     = t.rr && t.rr !== '0.00' ? `${t.rr}R` : '—';
        const timeStr   = [t.time, t.exitTime].filter(Boolean).join(' → ') || '—';
        const imgBefore = t.imgBefore || t.img || '';
        const imgAfter  = t.imgAfter  || '';
        const imgSection = (imgBefore || imgAfter) ? `
          <div style="display:flex;gap:8px;margin:10px 0 6px;flex-wrap:wrap;">
            ${imgBefore ? `<div style="flex:1;min-width:120px;"><div style="font-size:9px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:rgba(200,192,168,.4);margin-bottom:4px;">BEFORE</div><img src="${imgBefore}" alt="Before chart" style="width:100%;max-height:200px;object-fit:contain;border:1px solid rgba(255,255,255,.08);background:rgba(0,0,0,.3);"/></div>` : ''}
            ${imgAfter  ? `<div style="flex:1;min-width:120px;"><div style="font-size:9px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:rgba(200,192,168,.4);margin-bottom:4px;">AFTER</div><img src="${imgAfter}" alt="After chart" style="width:100%;max-height:200px;object-fit:contain;border:1px solid rgba(255,255,255,.08);background:rgba(0,0,0,.3);"/></div>` : ''}
          </div>` : '';

        const metaItems = [
          ['Entry',    t.entry   || '—',   null],
          ['SL',       t.sl      || '—',   'rgba(217,79,79,.7)'],
          ['TP',       t.tp      || '—',   'rgba(61,190,115,.7)'],
          ['Lot',      t.lotSize || '—',   null],
          ['Session',  t.session || '—',   'rgba(201,168,76,.7)'],
          ['Emotion',  t.emotion || '—',   'rgba(201,168,76,.55)'],
        ].filter(([, v]) => v && v !== '—');

        const metaRow = metaItems.length ? `
          <div style="display:flex;flex-wrap:wrap;gap:7px;margin:10px 0 8px;">
            ${metaItems.map(([l, v, c]) => `
              <div class="nb-meta-pill">
                <span class="nb-meta-pill-label">${l}</span>
                <span class="nb-meta-pill-value" style="color:${c || 'rgba(240,234,214,.82)'};">${v}</span>
              </div>`).join('')}
          </div>` : '';

        return `
          <div class="nb-trade-card ${win?'win':'loss'}">
            <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:10px;margin-bottom:12px;">
              <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
                <span style="font-size:17px;font-weight:700;letter-spacing:-.01em;color:rgba(240,234,214,.96);">${t.pair || '—'}</span>
                <span style="font-size:9px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;padding:3px 9px;background:${dirClr}1A;border:1px solid ${dirClr}44;color:${dirClr};border-radius:6px;">${t.dir}</span>
                ${t.strat ? `<span style="font-size:9px;font-weight:600;letter-spacing:.07em;color:rgba(201,168,76,.58);text-transform:uppercase;background:rgba(201,168,76,.08);border:1px solid rgba(201,168,76,.18);padding:2px 8px;border-radius:5px;">${t.strat}</span>` : ''}
              </div>
              <div style="text-align:right;flex-shrink:0;">
                <div style="font-size:20px;font-weight:800;color:${pnlClr};letter-spacing:.01em;line-height:1;">${pnlStr}</div>
                <div style="font-size:11px;font-weight:600;color:rgba(201,168,76,.68);margin-top:3px;">${rrStr}</div>
                <div style="font-size:9px;color:rgba(200,192,168,.32);margin-top:2px;">${timeStr}</div>
              </div>
            </div>
            <div style="display:grid;grid-template-columns:${(imgBefore||imgAfter)?'1fr 1fr':'1fr'};gap:14px;align-items:start;">
              <div>${metaRow}</div>
              ${(imgBefore||imgAfter) ? `
              <div style="display:flex;gap:8px;flex-wrap:wrap;">
                ${imgBefore ? `<div style="flex:1;min-width:80px;"><img src="${imgBefore}" alt="Before" style="width:100%;max-height:160px;object-fit:contain;border:1px solid rgba(255,255,255,.07);border-radius:6px;background:rgba(0,0,0,.25);"/></div>` : ''}
                ${imgAfter  ? `<div style="flex:1;min-width:80px;"><img src="${imgAfter}"  alt="After"  style="width:100%;max-height:160px;object-fit:contain;border:1px solid rgba(255,255,255,.07);border-radius:6px;background:rgba(0,0,0,.25);"/></div>` : ''}
              </div>` : ''}
            </div>
          </div>`;
      }).join('');

      return `
        <div style="border:1px solid rgba(201,168,76,.18);background:rgba(201,168,76,.045);padding:12px 16px;margin-bottom:16px;border-radius:10px;">
          <div style="font-size:8.5px;font-weight:700;letter-spacing:.15em;text-transform:uppercase;color:rgba(201,168,76,.52);margin-bottom:7px;">📅 TRADE RECAP — ${date}</div>
          <div style="display:flex;align-items:center;gap:16px;flex-wrap:wrap;">
            <span style="font-size:20px;font-weight:800;color:${pnlColor};letter-spacing:-.01em;">${pnlSign}$${Math.abs(totalPnl).toFixed(2)} net</span>
            <span style="font-size:10px;color:rgba(200,192,168,.48);font-weight:500;">${trades.length} trade${trades.length > 1 ? 's' : ''} · ${wins}W ${trades.length - wins}L</span>
          </div>
        </div>
        ${tradeBlocks}
        <p><br></p>
        <h2>My Reflections</h2>
        <div class="nb-reflection-grid">
          <div class="nb-reflection-card" style="background:rgba(61,190,115,.03);border-color:rgba(61,190,115,.14);">
            <div class="nb-reflection-card-label" style="color:rgba(61,190,115,.58);"><span>✓</span> What I Did Well</div>
            <p class="nb-reflection-card-body"><br></p>
          </div>
          <div class="nb-reflection-card" style="background:rgba(201,168,76,.025);border-color:rgba(201,168,76,.13);">
            <div class="nb-reflection-card-label" style="color:rgba(201,168,76,.58);"><span>↗</span> To Improve</div>
            <p class="nb-reflection-card-body"><br></p>
          </div>
          <div class="nb-reflection-card" style="background:rgba(78,127,212,.025);border-color:rgba(78,127,212,.13);">
            <div class="nb-reflection-card-label" style="color:rgba(78,127,212,.58);"><span>💡</span> Lessons Learned</div>
            <p class="nb-reflection-card-body"><br></p>
          </div>
          <div class="nb-reflection-card" style="background:rgba(217,79,79,.025);border-color:rgba(217,79,79,.13);">
            <div class="nb-reflection-card-label" style="color:rgba(217,79,79,.58);"><span>📋</span> Rule Compliance</div>
            <p class="nb-reflection-card-body"><br></p>
          </div>
        </div>
        <p><br></p>
      `;
    };

    const noteTitle = `Trade Recap — ${prefillDate}`;
    const noteBody  = buildPrefillHTML(prefillTrades, prefillDate);

    const t = setTimeout(async () => {
      try {
        const data = await apiCreateNote({ title: noteTitle, content: noteBody });
        const note = dbToNote(data.note);
        note.history = [];
        setNotes(prev => [note, ...prev]);
        setActiveId(note.id);
        setTitle(note.title);
        setMobileView('editor');
        setTimeout(() => {
          if (editorRef.current) {
            editorRef.current.innerHTML = noteBody;
            editorRef.current.focus();
            const range = document.createRange();
            range.selectNodeContents(editorRef.current);
            range.collapse(false);
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
          }
        }, 120);
        pushToast({ type:'success', title:'Note created', message:`Recap for ${prefillDate} is ready — add your reflections.`, duration:4000 });
      } catch {
        pushToast({ type:'error', title:'Could not create note', message:'Please try again.', duration:4000 });
      }
    }, 400);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  const pinnedIds = useMemo(() => new Set(notes.filter(n => n.pinned).map(n => n.id)), [notes]);
  const active    = notes.find(n => n.id === activeId);
  const noteTags  = active?.tagIds || [];

  const sorted = useMemo(() => {
    let arr = [...notes];
    if (filterTagId) arr = arr.filter(n => (n.tagIds||[]).includes(filterTagId));
    if (search) {
      const q = search.toLowerCase();
      arr = arr.filter(n =>
        (n.title||'').toLowerCase().includes(q) ||
        (n.body||'').replace(/<[^>]+>/g,'').toLowerCase().includes(q)
      );
    }
    arr.sort((a,b) => {
      const pa = pinnedIds.has(a.id), pb = pinnedIds.has(b.id);
      if (pa !== pb) return pa ? -1 : 1;
      switch(sortBy) {
        case 'Title':   return (a.title||'').localeCompare(b.title||'');
        case 'Created': return (b.created||'').localeCompare(a.created||'');
        case 'Updated': return (b.updated||'').localeCompare(a.updated||'');
        case 'A-Z':     return (a.title||'').localeCompare(b.title||'');
        default:        return (b.updated||'').localeCompare(a.updated||'');
      }
    });
    return arr;
  }, [notes, search, sortBy, filterTagId, pinnedIds]);

  const flush = useCallback(() => {
    if (activeId && editorRef.current) {
      const body = sanitizeHTML(editorRef.current.innerHTML);
      setNotes(prev => prev.map(n => n.id === activeId
        ? { ...n, body, title, updated: new Date().toISOString() } : n
      ));
    }
  }, [activeId, title]);

  const save = useCallback((overTitle) => {
    clearTimeout(saveTimer.current);
    setSaveStatus('Saving…');
    saveTimer.current = setTimeout(async () => {
      if (!editorRef.current) return;
      const rawBody   = editorRef.current.innerHTML;
      const localBody = sanitizeHTML(rawBody);
      const dbBody    = serializeNoteHTML(rawBody);
      const t         = overTitle !== undefined ? overTitle : title;
      setNotes(prev => prev.map(n => {
        if (n.id !== activeId) return n;
        const history = [{ ts: Date.now(), body: n.body }, ...(n.history||[])].slice(0, 10);
        return { ...n, title:t, body:localBody, updated:new Date().toISOString(), history };
      }));
      try {
        await apiUpdateNote(activeId, { title: t, content: dbBody });
        setSaveStatus('All saved');
      } catch {
        setSaveStatus('Save failed');
        pushToast({ type:'error', title:'Auto-save failed', message:'Check your connection. Changes are held locally.', duration:4000 });
      }
    }, 800);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, title]);

  const newNote = async (body='', tmplTitle='Untitled Note') => {
    const safeBody = sanitizeHTML(body);
    const dbBody   = serializeNoteHTML(body);
    try {
      const data = await apiCreateNote({ title: tmplTitle, content: dbBody });
      const note  = dbToNote(data.note);
      note.history = [];
      setNotes(prev => [note, ...prev]);
      setActiveId(note.id); setTitle(note.title); setMobileView('editor');
      setTimeout(() => { if (editorRef.current) { editorRef.current.innerHTML = safeBody; hydrateImages(editorRef.current); updatePlaceholder(editorRef.current); editorRef.current.focus(); } }, 80);
    } catch {
      pushToast({ type:'error', title:'Could not create note', message:'Please try again.', duration:4000 });
    }
  };

  const selectNote = note => {
    flush();
    setActiveId(note.id); setTitle(note.title||''); setMobileView('editor');
    setShowHistory(false);
    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.innerHTML = sanitizeHTML(note.body||'');
        hydrateImages(editorRef.current);
        updatePlaceholder(editorRef.current);
        editorRef.current.focus();
        const range = document.createRange();
        range.selectNodeContents(editorRef.current);
        range.collapse(false);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
    }, 60);
  };

  const backToList = () => { flush(); setMobileView('list'); };

  const confirmDeleteNote = async () => {
    const dt = title||'Untitled';
    setDeleteConfirm(false);
    setNotes(prev => prev.filter(n => n.id !== activeId));
    setActiveId(null); setTitle(''); setMobileView('list');
    if (editorRef.current) editorRef.current.innerHTML = '';
    try {
      await apiDeleteNote(activeId);
      pushToast({ type:'success', title:'Note deleted', message:`"${dt}" was permanently removed.`, duration:3500 });
    } catch {
      apiListNotes().then(data => setNotes((data.notes||[]).map(dbToNote))).catch(()=>{});
      pushToast({ type:'error', title:'Delete failed', message:'Note was restored. Please try again.', duration:4000 });
    }
  };

  const togglePin = async (id) => {
    const note = notes.find(n => n.id === id);
    if (!note) return;
    const newPinned = !note.pinned;
    setNotes(prev => prev.map(n => n.id === id ? { ...n, pinned: newPinned } : n));
    try {
      await apiUpdateNote(id, { title: note.title, content: note.body, pinned: newPinned });
    } catch {
      setNotes(prev => prev.map(n => n.id === id ? { ...n, pinned: note.pinned } : n));
      pushToast({ type:'error', title:'Could not update pin', message:'Please try again.', duration:3000 });
    }
  };

  const duplicateNote = async () => {
    if (!activeId) return;
    const src = notes.find(n => n.id === activeId);
    if (!src) return;
    try {
      const data = await apiCreateNote({ title: `${src.title||'Untitled'} (copy)`, content: src.body || '' });
      const note  = dbToNote(data.note);
      setNotes(prev => [note, ...prev]);
      pushToast({ type:'info', title:'Note duplicated', message:'A copy was created.', duration:2500 });
    } catch {
      pushToast({ type:'error', title:'Could not duplicate', message:'Please try again.', duration:3000 });
    }
  };

  const createTag  = (name, color) => { const tag = { id: Date.now(), name, color }; setAllTags(prev => [...prev, tag]); return tag; };
  const saveNoteTags = tagIds => { setNotes(prev => prev.map(n => n.id === activeId ? { ...n, tagIds } : n)); };

  const restoreVersion = v => {
    if (!editorRef.current) return;
    editorRef.current.innerHTML = sanitizeHTML(v.body||'');
    hydrateImages(editorRef.current);
    updatePlaceholder(editorRef.current);
    save();
    setShowHistory(false);
    pushToast({ type:'info', title:'Version restored', message:'Content from that save has been loaded.', duration:3000 });
  };

  const exec    = cmd => { document.execCommand(cmd, false, null); save(); };
  const getBody = ()  => sanitizeHTML(editorRef.current ? editorRef.current.innerHTML : '');

  const updatePlaceholder = (el) => {
    if (!el) return;
    const isEmpty = el.innerText.trim() === '' && !el.querySelector('img,.nb-img-block');
    el.setAttribute('data-empty', isEmpty ? 'true' : 'false');
  };

  const onInput = () => { updatePlaceholder(editorRef.current); save(); };
  const onTitle = e  => { setTitle(e.target.value); save(e.target.value); };

  const hydrateImages = (editor) => {
    if (!editor) return;
    editor.querySelectorAll('.nb-img-block').forEach((block, idx) => {
      [...block.childNodes].forEach(child => { if (child.nodeName !== 'IMG') child.remove(); });
      const nbId = block.getAttribute('data-nb-id') || ('nb' + Date.now() + '_' + idx + '_' + Math.random().toString(36).slice(2,7));
      block.setAttribute('data-nb-id', nbId);
      block.setAttribute('data-nb-img', '1');
      block.contentEditable = 'false';

      const label = document.createElement('div');
      label.className = 'nb-img-label';
      label.contentEditable = 'false';

      const captionSpan = document.createElement('span');
      captionSpan.className = 'nb-img-caption';
      captionSpan.textContent = 'CHART';

      const controls = document.createElement('div');
      controls.className = 'nb-img-controls';

      const expandBtn = document.createElement('button');
      expandBtn.className = 'nb-img-ctrl-btn expand nb-img-expand';
      expandBtn.type = 'button';
      expandBtn.setAttribute('data-nb-id', nbId);
      expandBtn.innerHTML = '<svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style="flex-shrink:0"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg><span>Expand</span>';

      const sepEl = document.createElement('div');
      sepEl.className = 'nb-img-ctrl-sep';

      const rmBtn = document.createElement('button');
      rmBtn.className = 'nb-img-ctrl-btn remove nb-img-rm';
      rmBtn.type = 'button';
      rmBtn.innerHTML = '<svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style="flex-shrink:0"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg><span>Remove</span>';

      controls.appendChild(expandBtn);
      controls.appendChild(sepEl);
      controls.appendChild(rmBtn);
      label.appendChild(captionSpan);
      label.appendChild(controls);
      block.appendChild(label);
    });
  };

  const handleImgClick = useCallback((e) => {
    const editor = editorRef.current;
    if (!editor) return;
    const expandBtn = e.target.closest('.nb-img-expand');
    if (expandBtn) {
      e.preventDefault(); e.stopPropagation();
      const nbId = expandBtn.getAttribute('data-nb-id');
      const block = nbId ? editor.querySelector(`.nb-img-block[data-nb-id="${nbId}"]`) : expandBtn.closest('.nb-img-block');
      if (block) block.classList.toggle('nb-img-expanded');
      return;
    }
    const rmBtn = e.target.closest('.nb-img-rm');
    if (rmBtn) {
      e.preventDefault(); e.stopPropagation();
      const block = rmBtn.closest('.nb-img-block');
      if (block) { block.remove(); onInput(); }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    hydrateImages(editor);
    editor.addEventListener('mousedown', handleImgClick);
    return () => editor.removeEventListener('mousedown', handleImgClick);
  }, [activeId, handleImgClick]);

  const compressImage = (dataUrl, maxW = 1200, quality = 0.8) =>
    new Promise(resolve => {
      const imgEl = new Image();
      imgEl.onload = () => {
        const scale = Math.min(1, maxW / imgEl.width);
        const w = Math.round(imgEl.width  * scale);
        const h = Math.round(imgEl.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(imgEl, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      imgEl.onerror = () => resolve(dataUrl);
      imgEl.src = dataUrl;
    });

  const insertImage = e => {
    const file = e.target.files[0]; if (!file) return;
    if (!file.type.startsWith('image/')) return;
    e.target.value = '';
    const doInsert = src => {
      const editor = editorRef.current; if (!editor) return;
      editor.focus();
      const nbId = 'nb' + Date.now() + Math.random().toString(36).slice(2, 7);
      const block = document.createElement('div');
      block.className = 'nb-img-block';
      block.setAttribute('data-nb-id', nbId);
      block.setAttribute('data-nb-img', '1');
      block.contentEditable = 'false';
      const img = document.createElement('img');
      img.src = src; img.className = 'nb-img'; img.alt = '';
      const label = document.createElement('div');
      label.className = 'nb-img-label';
      label.contentEditable = 'false';
      const nameSpan = document.createElement('span');
      nameSpan.className = 'nb-img-label-name';
      const captionSpan = document.createElement('span');
      captionSpan.className = 'nb-img-caption';
      captionSpan.textContent = 'CHART';
      const controls = document.createElement('div');
      controls.className = 'nb-img-controls';
      const expandBtn = document.createElement('button');
      expandBtn.className = 'nb-img-ctrl-btn expand nb-img-expand';
      expandBtn.innerHTML = '<svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style="flex-shrink:0"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg><span>Expand</span>';
      expandBtn.setAttribute('data-nb-id', nbId);
      expandBtn.type = 'button';
      const sepEl = document.createElement('div');
      sepEl.className = 'nb-img-ctrl-sep';
      const rmBtn = document.createElement('button');
      rmBtn.className = 'nb-img-ctrl-btn remove nb-img-rm';
      rmBtn.innerHTML = '<svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style="flex-shrink:0"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg><span>Remove</span>';
      rmBtn.type = 'button';
      controls.appendChild(expandBtn); controls.appendChild(sepEl); controls.appendChild(rmBtn);
      label.appendChild(nameSpan); label.appendChild(captionSpan); label.appendChild(controls);
      block.appendChild(img); block.appendChild(label);
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0 && editor.contains(sel.anchorNode)) {
        const range = sel.getRangeAt(0); range.collapse(false); range.insertNode(block);
        const newP = document.createElement('p'); newP.innerHTML = '<br/>'; block.after(newP);
        const nr = document.createRange(); nr.setStart(newP, 0); nr.collapse(true);
        sel.removeAllRanges(); sel.addRange(nr);
      } else {
        editor.appendChild(block);
        const newP = document.createElement('p'); newP.innerHTML = '<br/>'; editor.appendChild(newP);
      }
      onInput();
    };
    const reader = new FileReader();
    reader.onload = async ev => {
      setSaveStatus('Uploading…');
      try {
        const compressed = await compressImage(ev.target.result);
        let uploadedUrl = null;
        try {
          const res = await api('/uploads/image', { method: 'POST', body: JSON.stringify({ data: compressed }) });
          if (res?.url) {
            const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:5000').replace(/\/api$/, '');
            uploadedUrl = res.url.startsWith('http') ? res.url : `${API_BASE}${res.url}`;
          }
        } catch (uploadErr) {
          console.warn('[insertImage] upload endpoint failed, falling back to base64:', uploadErr);
        }
        const src = uploadedUrl ?? compressed;
        doInsert(src);
        setSaveStatus('All saved');
      } catch (err) {
        console.error('[insertImage]', err);
        setSaveStatus('All saved');
        pushToast({ type:'error', title:'Image insert failed', message:'Could not process the image. Try a smaller file.', duration:4000 });
      }
    };
    reader.readAsDataURL(file);
  };

  const copyAsMarkdown = () => {
    if (!editorRef.current) return;
    const txt = editorRef.current.innerText || '';
    navigator.clipboard.writeText(`# ${title}\n\n${txt}`).then(() =>
      pushToast({ type:'success', title:'Copied as Markdown', message:'Pasted into any markdown-ready app.', duration:2500 })
    );
  };

  useEffect(() => {
    const t = setTimeout(() => updatePlaceholder(editorRef.current), 100);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const [currentWC, setCurrentWC] = useState(0);
  useEffect(() => {
    const txt = editorRef.current?.innerText || '';
    setCurrentWC(txt.split(/\s+/).filter(Boolean).length);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, saveStatus]);
  const goalPct = wordGoal ? Math.min(100, Math.round(currentWC / wordGoal * 100)) : 0;

  const listCls   = `nb-list-pane${mobileView==='list'?' nb-list-show':' nb-list-hide'}`;
  const editorCls = `nb-editor-col${mobileView==='editor'?' nb-editor-show':' nb-editor-hide'}`;

  const renderNoteItem = n => {
    const isCompact = viewMode === 'compact';
    const isPinned  = pinnedIds.has(n.id);
    const nTags     = (n.tagIds||[]).map(id => allTags.find(t=>t.id===id)).filter(Boolean);
    const rawPreview = (n.body||'').replace(/<[^>]+>/g,'').replace(/\s+/g,' ').trim();
    const preview    = rawPreview.slice(0, isCompact ? 42 : 52) || 'No content';
    const dateStr    = n.updated ? new Date(n.updated).toLocaleDateString('en-US',{month:'short',day:'numeric'}) : '';
    return (
      <div key={n.id}
        className={`nb-note-item${n.id===activeId?' active':''}${isPinned?' pinned':''}`}
        role="listitem" tabIndex={0}
        onClick={()=>selectNote(n)}
        onKeyDown={e=>{ if(e.key==='Enter'||e.key===' '){ e.preventDefault(); selectNote(n); } }}>
        <div className="nb-note-item-title-row">
          {isPinned && (
            <svg width="9" height="9" viewBox="0 0 24 24" fill="var(--gold)" stroke="none" style={{ flexShrink:0, opacity:.7 }}><path d="M12 2l2.4 4.9L20 7.9l-4 3.9.9 5.5L12 14.8l-4.9 2.5.9-5.5L4 7.9l5.6-.9z"/></svg>
          )}
          <div className="nb-note-item-title nb-note-item-title-flex">{n.title||'Untitled'}</div>
          {!isCompact && (
            <button
              className="nb-pin-icon nb-pin-btn"
              onClick={ev=>{ev.stopPropagation();togglePin(n.id);}}
              style={{ color:isPinned?'var(--gold)':'rgba(240,234,214,.22)' }}
              title={isPinned?'Unpin':'Pin note'}>
              <svg width="9" height="9" viewBox="0 0 24 24" fill={isPinned?'var(--gold)':'none'} stroke="currentColor" strokeWidth="2"><path d="M12 2l2.4 4.9L20 7.9l-4 3.9.9 5.5L12 14.8l-4.9 2.5.9-5.5L4 7.9l5.6-.9z"/></svg>
            </button>
          )}
        </div>
        {!isCompact && <div className="nb-note-item-preview">{preview}</div>}
        {nTags.length > 0 && (
          <div className="nb-note-item-tags">
            {nTags.slice(0,3).map(tag => <TagBadge key={tag.id} label={tag.name} color={tag.color} small/>)}
          </div>
        )}
        {!isCompact && dateStr && <div className="nb-note-item-date">{dateStr}</div>}
      </div>
    );
  };

  return (
    <>
    <div className="nb-layout">

      {/* ════════════ LIST PANE ════════════ */}
      <div className={listCls}>

        {/* Header */}
        <div className="nb-list-header">
          <div className="nb-list-header-top">
            <div className="nb-list-heading">
              Notes
              <span className="nb-list-count-badge">{notes.length}</span>
            </div>
            <button className="btn-gold nb-list-new-btn" onClick={()=>newNote()}>+ New</button>
          </div>

          {/* Search */}
          <div className="nb-search-wrap">
            <svg className="nb-search-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input
              value={search}
              onChange={e=>setSearch(e.target.value)}
              placeholder="Search notes…"
              className="nb-search-input"
            />
            {search && <button onClick={()=>setSearch('')} className="nb-search-clear">×</button>}
          </div>

          {/* Sort + View mode */}
          <div className="nb-sort-view-row">
            <div className="nb-sort-group">
              {SORT_OPTIONS.map(s => (
                <button key={s} className={`nb-ctrl-btn${sortBy===s?' active':''}`} onClick={()=>setSortBy(s)}>{s}</button>
              ))}
            </div>
            <div className="nb-view-group">
              {[['list','≡'],['compact','⊟']].map(([m,icon]) => (
                <button key={m} className={`nb-ctrl-btn${viewMode===m?' active':''}`} onClick={()=>setViewMode(m)} title={m}>{icon}</button>
              ))}
            </div>
          </div>
        </div>

        {/* Tag filters */}
        {allTags.length > 0 && (
          <div className="nb-tag-filters">
            <button className={`nb-tag-filter-btn${!filterTagId?' active':''}`} onClick={()=>setFilterTagId(null)}>All</button>
            {allTags.map(tag => (
              <button
                key={tag.id}
                className={`nb-tag-filter-btn${filterTagId===tag.id?' active':''}`}
                onClick={()=>setFilterTagId(filterTagId===tag.id?null:tag.id)}
                style={filterTagId===tag.id?{background:`${tag.color}18`,borderColor:`${tag.color}44`,color:tag.color}:{}}>
                {tag.name}
              </button>
            ))}
          </div>
        )}

        {/* Note list */}
        <div className="nb-note-list">
          {isLoading ? (
            <SkeletonList/>
          ) : (
            <>
              {sorted.length===0 && notes.length>0 && <div className="nb-no-results">No results.</div>}
              {sorted.map(n => renderNoteItem(n))}

              {notes.length===0 && (
                <div className="nb-empty-state">
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="1.2" style={{opacity:.5}}><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
                  <div className="nb-empty-templates">
                    {NOTE_TEMPLATES.map(t => (
                      <button key={t.title} onClick={()=>newNote(t.body,t.title)} className="nb-template-btn">
                        <span className="nb-template-icon">{t.icon}</span>
                        <span className="nb-template-label">{t.title}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer note count */}
        {!isLoading && notes.length > 0 && (
          <div className="nb-list-footer">{sorted.length} of {notes.length} notes</div>
        )}
      </div>

      {/* ════════════ EDITOR COLUMN ════════════ */}
      <div className={editorCls}>
        {!activeId ? (
          <div className="nb-editor-empty">
            <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="1.2"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
            <h3 className="nb-editor-empty-title">Trading Notebook</h3>
            <p className="nb-editor-empty-sub">Plan sessions, capture ideas, track growth.<br/>Choose a template to get started instantly.</p>
            <div className="nb-editor-empty-templates">
              {NOTE_TEMPLATES.map(t => (
                <button key={t.title} onClick={()=>newNote(t.body,t.title)} className="nb-editor-template-btn">
                  <span className="nb-editor-template-icon">{t.icon}</span>
                  <span className="nb-template-label">{t.title}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* ── TOP TOOLBAR ── */}
            <div className="nb-toolbar-top">
              <div className="nb-toolbar-zone-a">
                <button className="nb-back-btn" onClick={backToList} style={{display:'flex'}}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
                  <span>Notes</span>
                </button>
                <div className="nb-toolbar-title">{title||'Untitled'}</div>
                <div className="nb-save-pill">
                  <div className="nb-save-dot" style={{ background: saveStatus==='All saved' ? 'var(--green)' : 'var(--gold)', boxShadow: `0 0 4px ${saveStatus==='All saved' ? 'var(--green)' : 'var(--gold)'}` }}/>
                  {saveStatus}
                </div>
              </div>

              <div className="nb-toolbar-zone-b">
                <button className={`nb-action-btn nb-action-pin${pinnedIds.has(activeId)?' pinned':''}`} onClick={()=>togglePin(activeId)} aria-label={pinnedIds.has(activeId)?'Unpin note':'Pin note'} title={pinnedIds.has(activeId)?'Unpin':'Pin note'}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                  <span className="nb-btn-label">{pinnedIds.has(activeId)?'Pinned':'Pin'}</span>
                </button>
                <button className="nb-action-btn nb-action-tag" aria-label="Manage tags" onClick={()=>setShowTagModal(true)}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
                  <span className="nb-btn-label">Tags</span>
                </button>
                <button className={`nb-action-btn nb-action-hist${showHistory?' active':''}`} aria-label="Version history" onClick={()=>setShowHistory(v=>!v)}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-4.2"/></svg>
                  <span className="nb-btn-label">History</span>
                </button>
                <button className="nb-action-btn nb-action-dupe nb-mobile-hide" onClick={duplicateNote} aria-label="Duplicate note" title="Duplicate note">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                  Dupe
                </button>
              </div>

              <div className="nb-toolbar-zone-c">
                <ExportDropdown title={title} getBody={getBody} copyAsMarkdown={copyAsMarkdown} exportTXT={exportTXT} exportPDF={exportPDF} pushToast={pushToast}/>
                <div className="nb-action-del-zone">
                  <button className="nb-action-btn nb-action-del" aria-label="Delete note" onClick={()=>setDeleteConfirm(true)}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                    <span className="nb-del-label">Delete</span>
                  </button>
                </div>
              </div>
            </div>

            {/* ── FORMAT TOOLBAR ── */}
            <div className="nb-toolbar-fmt">
              {[
                ['B','bold','nb-fmt-bold','Bold'],
                ['I','italic','nb-fmt-italic','Italic'],
                ['U','underline','nb-fmt-underline','Underline'],
              ].map(([l,cmd,cls,label])=>(
                <button key={cmd} className={`nb-fmt-btn ${cls}`} aria-label={label} onMouseDown={e=>e.preventDefault()} onClick={()=>exec(cmd)}>{l}</button>
              ))}
              <div className="nb-fmt-sep"/>
              <button className="nb-fmt-btn nb-fmt-wide" aria-label="Bullet list" onMouseDown={e=>e.preventDefault()} onClick={()=>exec('insertUnorderedList')}>• List</button>
              <button className="nb-fmt-btn nb-fmt-wide" aria-label="Numbered list" onMouseDown={e=>e.preventDefault()} onClick={()=>exec('insertOrderedList')}>1. List</button>
              <button className="nb-fmt-btn nb-fmt-h2" aria-label="Heading 2" onMouseDown={e=>e.preventDefault()} onClick={()=>{ const a=editorRef.current; if(!a)return; a.focus(); document.execCommand('formatBlock',false,'h2'); onInput(); }}>H2</button>
              <button className="nb-fmt-btn nb-fmt-strike" aria-label="Strikethrough" onMouseDown={e=>e.preventDefault()} onClick={()=>exec('strikeThrough')}>S̶</button>
              <button className="nb-fmt-btn nb-fmt-clear" aria-label="Clear formatting" onMouseDown={e=>e.preventDefault()} onClick={()=>exec('removeFormat')} title="Clear formatting">✕ Fmt</button>
              <div className="nb-fmt-sep"/>
              <input ref={imgInputRef} type="file" accept="image/*" onChange={insertImage} style={{display:'none'}}/>
              <button className="nb-fmt-btn nb-fmt-img" aria-label="Insert image" title="Insert chart image" onClick={()=>imgInputRef.current?.click()}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                <span className="nb-fmt-img-label">Image</span>
              </button>
              <div className="nb-fmt-sep"/>
              {/* Word goal */}
              {showGoalInput ? (
                <form className="nb-goal-input-form" onSubmit={e => {
                  e.preventDefault();
                  const g = parseInt(goalInputVal, 10);
                  setWordGoal(isFinite(g) && g > 0 ? g : null);
                  setShowGoalInput(false);
                  setGoalInputVal('');
                }}>
                  <input
                    autoFocus
                    type="number"
                    min="1"
                    value={goalInputVal}
                    onChange={e => setGoalInputVal(e.target.value)}
                    onBlur={() => { setShowGoalInput(false); setGoalInputVal(''); }}
                    onKeyDown={e => { if (e.key === 'Escape') { setShowGoalInput(false); setGoalInputVal(''); } }}
                    placeholder="500"
                    className="nb-goal-input"
                  />
                  <button type="submit" className="nb-fmt-btn nb-goal-btn-confirm">✓</button>
                </form>
              ) : (
                <button className="nb-fmt-btn nb-goal-btn-toggle" onClick={() => {
                  if (wordGoal) { setWordGoal(null); } else { setShowGoalInput(true); }
                }} title={wordGoal ? 'Click to clear goal' : 'Set word count goal'}>
                  {wordGoal ? `${currentWC}/${wordGoal}` : 'Goal'}
                </button>
              )}
            </div>

            {/* Word goal bar */}
            {wordGoal && (
              <div className="nb-goal-bar-wrap">
                <div className="nb-goal-bar-meta">
                  <span className="nb-goal-bar-label">WORD GOAL</span>
                  <span className={`nb-goal-bar-pct ${goalPct>=100?'done':'progress'}`}>{goalPct}%</span>
                  <span className="nb-goal-bar-count">{currentWC} / {wordGoal} words</span>
                </div>
                <div className="nb-goal-bar-track">
                  <div className={`nb-goal-bar-fill ${goalPct>=100?'done':'progress'}`} style={{ width:`${goalPct}%` }}/>
                </div>
              </div>
            )}

            {/* ── TITLE AREA ── */}
            <div className="nb-title-area">
              <input
                value={title}
                onChange={onTitle}
                placeholder="Note title…"
                className="nb-title-input"
              />
              <NoteStats editorRef={editorRef} note={active}/>
              {noteTags.length > 0 && (
                <div className="nb-note-tags-row">
                  {noteTags.map(id => { const tag=allTags.find(t=>t.id===id); return tag ? <TagBadge key={id} label={tag.name} color={tag.color} onRemove={()=>saveNoteTags(noteTags.filter(x=>x!==id))}/> : null; })}
                </div>
              )}
            </div>

            {/* ── EDITOR BODY ── */}
            <div
              className="nb-body-area"
              onClick={e => { const ed = editorRef.current; if (!ed) return; if (e.target === e.currentTarget) { ed.focus(); const r = document.createRange(); r.selectNodeContents(ed); r.collapse(false); const s = window.getSelection(); s?.removeAllRanges(); s?.addRange(r); } }}
            >
              <div className="nb-edit-hint" aria-hidden="true">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                <span>Click to edit</span>
              </div>
              <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                onInput={onInput}
                className="nb-area"
                data-placeholder="Type your trading notes here…"
              />
            </div>

            {/* ── FOOTER ── */}
            <div className="nb-footer">
              <div className="nb-footer-autosave">Auto-saved every 800ms</div>
              <span className="nb-footer-hint">PDF → browser print → Save as PDF</span>
            </div>

            {/* History panel */}
            {showHistory && (
              <HistoryPanel history={active?.history||[]} onRestore={restoreVersion} onClose={()=>setShowHistory(false)}/>
            )}
          </>
        )}
      </div>
    </div>

    {/* ── MODALS / OVERLAYS ── */}
    {deleteConfirm && <ConfirmModal noteTitle={title} onConfirm={confirmDeleteNote} onCancel={()=>setDeleteConfirm(false)}/>}
    {showTagModal  && (
      <TagModal
        allTags={allTags}
        noteTags={noteTags}
        onSave={saveNoteTags}
        onClose={()=>setShowTagModal(false)}
        onCreateTag={createTag}
      />
    )}
    <ToastStack toasts={toasts} onDismiss={dismissToast}/>
    </>
  );
}